import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Center, Spinner } from "@chakra-ui/react";
import { useAuth } from "../../contexts/AuthContext";
import ShopAccessPending from "../ShopRoute/ShopAccessPending";

// Dashboard routes a customer without trials access may still reach: the
// storefront (gated on shop access by ShopGate) plus their own account pages.
// Deliberately an allowlist, not a denylist — a clinical page added later stays
// closed to shop customers until it is listed here on purpose.
const CUSTOMER_PATHS = [
  "/dashboard/shop",
  "/dashboard/cart",
  "/dashboard/checkout",
  "/dashboard/my-orders",
  "/dashboard/support",
  "/dashboard/settings",
];

// The dashboard index is exact-match only: DashboardHome renders the shop view
// for these users, but "/dashboard" as a prefix would match every clinical page
// under it.
const isCustomerPath = (pathname) =>
  pathname === "/dashboard" ||
  CUSTOMER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// Guards the dashboard. Requires a session; the clinical pages additionally
// require trials access (profiles.trials_access — admins and, as a rollout
// safety valve, sessions where the profile table can't be reached count as
// having it; see `trialsAccess` in AuthContext).
//
// Shop-only customers must still reach the storefront, which now lives under
// /dashboard. Redirecting them out of the subtree is what caused the white
// screen: they were sent to /order, which redirects to /dashboard/shop, which
// bounced them back to /order forever. So let them through on the storefront
// paths and land them there from anywhere else in the dashboard.
const ProtectedRoute = ({ children }) => {
  const { session, loading, profileLoading, trialsAccess, shopAccess, profile } =
    useAuth();
  const location = useLocation();

  if (loading || (session && profileLoading)) {
    return (
      <Center minH="100vh">
        <Spinner size="lg" thickness="3px" color="blue.500" />
      </Center>
    );
  }

  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (!trialsAccess) {
    // No dashboard access of any kind — show the pending/declined screen rather
    // than redirecting anywhere, so there is nothing left to bounce between.
    if (!shopAccess) {
      return <ShopAccessPending status={profile?.status} />;
    }
    return isCustomerPath(location.pathname) ? (
      children
    ) : (
      <Navigate to="/dashboard/shop" replace />
    );
  }

  return children;
};

export default ProtectedRoute;
